import React from "react"
import ModalForm from "components/dialogs/ReactHookForm/ModalForm"
import {
  Box, Flex, Stack, Text,
} from "@chakra-ui/react"
import ConfigBlock from "pages/EventShow/Forms/StyleAndConfiguration/ConfigBlock"
import ImageUpload from "components/ReactHookForm/ImageUpload"
import { DioboxEvent } from "sharedTypes"
import extendFormValuesWithImages from "utilities/extendFormValuesWithImages"
import ImageUploadComponent from "components/ImageUploadComponent"
import UrlField from "components/ReactHookForm/UrlField"
import { VIDEO_URL_REGEX } from "components/VideoEmbed"
import Scale from "components/ReactHookForm/Scale"
import PreviewFrameWithBranding from "pages/EventShow/Forms/StyleAndConfiguration/PortalPreview/PreviewFrameWithBranding/PreviewFrameWithBranding"

type Props = {
  onHide: () => void
  event: DioboxEvent
  updateEvent: (params: {}) => Promise<void>
  onChangeEventWorkingCopy: (values: Partial<DioboxEvent>) => void
  onResetEventWorkingCopy: () => void
}

const EventImageAndVideoModal = ({
  onHide,
  event: {
    portalEventImageUrl,
    portalEventVideoUrl,
    portalEventImageScale,
    portalShowEventImage,
    portalShowEventVideo,
  },
  event,
  updateEvent,
  onChangeEventWorkingCopy,
  onResetEventWorkingCopy,
}: Props) => {
  const values = {
    portalEventImageUrl,
    portalEventVideoUrl,
    portalEventImageScale,
    portalShowEventImage,
    portalShowEventVideo,
  }

  const handleSubmit = async (updatedValues: DioboxEvent) => {
    await updateEvent({
      ...updatedValues,
      portalEventVideoUrl:
      updatedValues.portalShowEventVideo
        ? updatedValues.portalEventVideoUrl
        : null,
    })
    onHide()
  }

  const handleCancel = () => {
    onResetEventWorkingCopy()
    onHide()
  }

  return (
    <ModalForm
      initialValues={extendFormValuesWithImages(values, ["portalEventImage"])}
      title="Event Image and Video"
      onSubmit={handleSubmit}
      onClose={handleCancel}
      onChange={onChangeEventWorkingCopy}
      disableSubmitWhenNotDirty
      size="5xl"
    >
      {({ control, watch }) => {
        const eventImageEnabled = watch("portalShowEventImage")
        const eventVideoEnabled = watch("portalShowEventVideo")
        const formEventImageUrlValue = watch("portalEventImageUrl")

        return (
          <Flex m={-8}>
            <Flex w="50%" borderRightWidth={1}>
              <PreviewFrameWithBranding event={event} onSelectModal={() => {}} />
            </Flex>
            <Flex w="50%">
              <Stack py={4} spacing={4}>
                <ConfigBlock
                  title="Event Image"
                  switchName="portalShowEventImage"
                  control={control}
                  showSeparator
                >
                  <Text>Upload a branded image that reflects your event and theme.</Text>
                  <Box mt={4} mb={4}>
                    <ImageUpload
                      name="portalEventImage"
                      control={control}
                      imageWidth="auto"
                      imageHeight={12}
                      imageUrl={portalEventImageUrl ? `${portalEventImageUrl}?w=320` : ""}
                      Component={UploadComponent}
                      disabled={!eventImageEnabled}
                    />
                  </Box>
                  {formEventImageUrlValue && (
                  <Box mt={6} maxW={96}>
                    <Scale control={control} name="portalEventImageScale" disabled={!eventImageEnabled} />
                  </Box>
                  )}
                </ConfigBlock>
                <ConfigBlock
                  title="Event Video"
                  switchName="portalShowEventVideo"
                  control={control}
                >
                  <Text>
                    Include a video instead of the event image. The video will
                    autoplay on blank forms.
                  </Text>
                  <Box mt={4} mb={4}>
                    <UrlField
                      variant="outline"
                      name="portalEventVideoUrl"
                      control={control}
                      disabled={!eventVideoEnabled}
                      helperText="Paste a YouTube or Vimeo link."
                      {...eventVideoEnabled && { urlValidatorRegex: VIDEO_URL_REGEX }}
                      errorMessage="That URL does not seem to be a valid video link."
                    />
                  </Box>
                </ConfigBlock>
              </Stack>
            </Flex>
          </Flex>
        )
      }}
    </ModalForm>
  )
}

const UploadComponent = (props) => <ImageUploadComponent {...props} uploadButtonText="Upload Event Image" removeButtonText="Remove" />

export default EventImageAndVideoModal
